import { type ReactNode } from "react";
import { DateTime } from "luxon";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  HeavyWarningIcon,
  InfoIcon,
  LoaderIcon,
} from "../../../assets/icons";
import GroupTable from "./GroupTable";
import { BulkParseStatus } from "./constants";

type BulkErrorRow = Readonly<{ row?: number; message?: string }>;

type BulkSuccessRow = Readonly<{
  invoiceNo?: string;
  invoiceAmount?: number;
  requestedAmount?: number;
  issuedDate?: string;
  dueDate?: string;
}>;

type BulkParsePanelsState = Readonly<{
  status: BulkParseStatus;
  errors?: readonly BulkErrorRow[];
  successes?: readonly BulkSuccessRow[];
}>;

type DrawdownFormBulkParsePanelsProps = Readonly<{
  currency: string;
  bulkParseState: BulkParsePanelsState;
  bulkInvoicesExpanded: boolean;
  onToggleInvoicesExpanded: () => void;
  parseDateValueForUI: (value?: string) => Date | undefined;
}>;

function bulkParseErrorLine(e: Readonly<BulkErrorRow>, idx: number): ReactNode {
  const fallback = e.message ?? `Row ${e.row ?? idx + 2} - Invalid row data`;
  const separatorIdx = fallback.indexOf(" - ");
  if (separatorIdx === -1) {
    return (
      <span className="cx-text-xs cx-text-text-primary cx-font-medium">
        {fallback}
      </span>
    );
  }
  const rowLabel = fallback.slice(0, separatorIdx + 3);
  const details = fallback.slice(separatorIdx + 3);
  return (
    <span className="cx-text-xs cx-font-medium">
      <span className="cx-text-text-error">{rowLabel}</span>
      <span className="cx-text-text-primary">{details}</span>
    </span>
  );
}

export function DrawdownFormBulkParsePanels(
  props: DrawdownFormBulkParsePanelsProps
) {
  const {
    currency,
    bulkParseState,
    bulkInvoicesExpanded,
    onToggleInvoicesExpanded,
    parseDateValueForUI,
  } = props;
  if (bulkParseState.status === BulkParseStatus.EXTRACTING) {
    return (
      <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-6 cx-shadow-base">
        <div className="cx-rounded-lg cx-border cx-border-neutral-light cx-bg-white cx-p-6 cx-flex cx-flex-col cx-items-center cx-justify-center cx-gap-2">
          <LoaderIcon className="cx-w-12 cx-h-12 cx-animate-spin" />
          <div className="cx-font-semibold cx-text-text-primary">
            Extracting data from the file
          </div>
          <div className="cx-text-sm cx-text-text-tertiary cx-text-center">
            We&apos;re processing your file. This may take a few minutes, please
            don&apos;t refresh the page.
          </div>
        </div>
      </div>
    );
  }

  if (bulkParseState.status === BulkParseStatus.ERRORS) {
    return (
      <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-6 cx-shadow-base">
        <div className="cx-rounded-lg cx-border cx-border-neutral-light cx-bg-white cx-p-6">
          <div className="cx-flex cx-flex-col cx-items-center cx-justify-center cx-gap-2 cx-mb-5">
            <HeavyWarningIcon
              width={28}
              height={26}
              className="cx-text-text-error"
            />
            <div className="cx-font-semibold cx-text-text-primary cx-text-center">
              We found issues in your file
            </div>
            <div className="cx-text-sm cx-text-text-secondary cx-text-center cx-max-w-md">
              Please review the errors below and upload a corrected file.
            </div>
          </div>

          <div className="cx-bg-gray-50 cx-rounded-lg cx-border cx-border-gray-200 cx-p-2">
            <div className="cx-max-h-64 cx-overflow-y-auto">
              {bulkParseState.errors?.map((e, idx) => (
                <div
                  key={`${e.row ?? idx}-${idx}`}
                  className="cx-flex cx-items-start cx-gap-2 cx-px-2 cx-py-3 cx-border-b last:cx-border-b-0 cx-border-gray-200"
                >
                  <InfoIcon
                    width={16}
                    height={16}
                    className="cx-text-text-error"
                  />
                  {bulkParseErrorLine(e, idx)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (bulkParseState.status === BulkParseStatus.SUCCESS) {
    return (
      <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-6 cx-shadow-base">
        <div
          className={`cx-border cx-border-neutral-light cx-bg-white cx-p-4 ${
            bulkInvoicesExpanded ? "cx-rounded-xl" : ""
          }`}
        >
          <button
            type="button"
            onClick={onToggleInvoicesExpanded}
            className="cx-w-full cx-flex cx-items-center cx-gap-3 cx-text-left"
          >
            <span className="cx-inline-flex cx-items-center cx-justify-center cx-w-5 cx-h-5 cx-rounded-full  cx-border cx-p-1 cx-border-gray-250 ">
              {bulkInvoicesExpanded ? (
                <ArrowUpIcon className="cx-w-6 cx-h-3 cx-text-[#335CFF]" />
              ) : (
                <ArrowDownIcon className="cx-w-6 cx-h-3 cx-text-[#335CFF]" />
              )}
            </span>
            <h3 className="cx-text-base cx-text-text-primary">
              All invoices data
            </h3>
          </button>
        </div>

        {bulkInvoicesExpanded && (
          <GroupTable
            columns={[
              { header: "Invoice# ", render: (row: any) => row.invoiceNo },
              {
                header: `Invoice amount <${currency}>`,
                render: (row: any) =>
                  row.invoiceAmount ? String(row.invoiceAmount) : "",
              },
              {
                header: `Requested amount <${currency}>`,
                render: (row: any) =>
                  row.requestedAmount ? String(row.requestedAmount) : "",
              },
              {
                header: "Issued date",
                render: (row: any) => {
                  const d = parseDateValueForUI(row.issuedDate);
                  return d
                    ? DateTime.fromJSDate(d).toFormat("d LLLL, yyyy")
                    : "";
                },
              },
              {
                header: "Due date",
                render: (row: any) => {
                  const d = parseDateValueForUI(row.dueDate);
                  return d
                    ? DateTime.fromJSDate(d).toFormat("d LLLL, yyyy")
                    : "";
                },
              },
            ]}
            items={(bulkParseState.successes ?? []).map((r, idx) => ({
              ...r,
              id: String(idx),
              invoiceNo: r.invoiceNo,
              invoiceAmount: r.invoiceAmount,
              requestedAmount: r.requestedAmount,
            }))}
          />
        )}
      </div>
    );
  }

  return null;
}
